/**
 * BaaS instance users & groups — MobileService Prod SQL.
 *
 * Membership lives on the Prod instance. HML lists the same Prod users
 * (carteira login is always Prod). Org: GET /v1/ib/baas/users|groups
 *         POST /v1/ib/baas/users  (multi-instance membership)
 * Per-instance: GET/POST/PATCH/DELETE .../instances/{id}/users|groups
 */

import { mobileConsoleRequest, resolveBaasProxyContext } from "./baasConsoleClient";

const MOCK_BAAS_NOTIFICATION_PREFS = {
  "valut-hml": [
    { id: "email-alerts", labelKey: "baas.notifications.emailAlerts", enabled: true },
    { id: "api-errors", labelKey: "baas.notifications.apiErrors", enabled: true },
    { id: "kyb-updates", labelKey: "baas.notifications.kybUpdates", enabled: true },
    { id: "payout-notifications", labelKey: "baas.notifications.payoutNotifications", enabled: false },
  ],
  valut: [
    { id: "email-alerts", labelKey: "baas.notifications.emailAlerts", enabled: true },
    { id: "api-errors", labelKey: "baas.notifications.apiErrors", enabled: true },
    { id: "kyb-updates", labelKey: "baas.notifications.kybUpdates", enabled: false },
    { id: "payout-notifications", labelKey: "baas.notifications.payoutNotifications", enabled: true },
  ],
};

const MOCK_BAAS_SETTINGS = {
  "valut-hml": {
    webhookUrl: "https://api.valut.com/webhooks/hml",
    timezone: "America/Sao_Paulo",
  },
  valut: {
    webhookUrl: "https://api.valut.com/webhooks/prod",
    timezone: "America/Sao_Paulo",
  },
};

export const BAAS_GROUP_PERMISSIONS = [
  "console.read",
  "console.write",
  "api.manage",
  "users.manage",
  "transactions.read",
  "transactions.write",
  "reports.read",
];

export const BAAS_GROUP_ROLE_PERMISSIONS = {
  super_user: [...BAAS_GROUP_PERMISSIONS],
  admin: [...BAAS_GROUP_PERMISSIONS],
  operator: ["console.read", "transactions.read", "transactions.write"],
  viewer: ["console.read", "reports.read"],
};

/** Console roles. `super_user` = platform master (all instances + all actions); `admin` = instance-scoped. */
export const BAAS_USER_ROLES = ["super_user", "admin", "operator", "viewer"];

/**
 * Roles an instance Administrador may assign (never platform master).
 * Only `super_user` (master) may offer / assign `super_user`.
 */
export const BAAS_SCOPED_USER_ROLES = ["admin", "operator", "viewer"];

/** @param {boolean} isMaster */
export const assignableBaasUserRoles = (isMaster) =>
  isMaster ? BAAS_USER_ROLES : BAAS_SCOPED_USER_ROLES;

/** Groups stay instance-scoped — no platform master super_user. */
export const BAAS_GROUP_ROLES = ["admin", "operator", "viewer"];

export const BAAS_USER_STATUSES = ["active", "invited", "suspended"];

const normalizeConsoleRole = (roleRaw, fallback = "viewer") => {
  const value = String(roleRaw ?? "").trim().toLowerCase();
  if (value === "super-user" || value === "superuser") return "super_user";
  if (value === "super_user" || value === "admin" || value === "operator" || value === "viewer") {
    return value;
  }
  return fallback;
};

const isProductionInstance = (item) => {
  const env = String(item?.environment || "").trim().toLowerCase();
  return item?.isProduction === true || env === "production" || env === "prod";
};

/**
 * Map a Prod SQL instance id to a console selection card.
 * Prefer production — `Number("9:development") === 9` must not steal the match
 * (that wrongly bound locked links to HML cards and broke edit/remove).
 */
const findInstanceByBaasId = (instances, baasInstanceId) => {
  const numeric = Number(baasInstanceId);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const matches = (instances ?? []).filter(
    (item) => Number(item?.baasInstanceId) === numeric,
  );
  if (matches.length === 0) return null;
  return matches.find((item) => isProductionInstance(item)) ?? matches[0];
};

const mapBaasUser = (row, instanceSelectionId) => {
  const numericInstanceId = Number(row?.instance_id ?? row?.instanceId);
  const statusRaw = String(row?.status ?? "active").trim().toLowerCase();
  const status =
    statusRaw === "active" || statusRaw === "invited" || statusRaw === "suspended"
      ? statusRaw
      : "suspended";
  // Unknown role must not default to admin (would show full permission checkboxes).
  const roleRaw = String(row?.role ?? "viewer").trim().toLowerCase();
  const role = normalizeConsoleRole(roleRaw, "viewer");
  const groupIdRaw = row?.group_id ?? row?.groupId;
  const groupId =
    groupIdRaw === null || groupIdRaw === undefined || groupIdRaw === ""
      ? null
      : String(groupIdRaw);
  const permissionsRaw = row?.permissions;
  const permissions = Array.isArray(permissionsRaw)
    ? permissionsRaw.map(String)
    : BAAS_GROUP_ROLE_PERMISSIONS[role] ?? [];

  const entityIdRaw = row?.entity_id ?? row?.entityId;
  const entityId =
    entityIdRaw != null && String(entityIdRaw).trim() !== ""
      ? String(entityIdRaw).trim()
      : null;

  const accessStatusRaw = String(row?.access_status ?? row?.accessStatus ?? "pending")
    .trim()
    .toLowerCase();
  const accessStatus =
    accessStatusRaw === "completed" ||
    accessStatusRaw === "pending" ||
    accessStatusRaw === "no_email"
      ? accessStatusRaw
      : "pending";

  return {
    id: String(row?.id ?? row?.baas_api_instance_users_id ?? ""),
    personId: Number(row?.person_id ?? row?.personId) || null,
    entityId,
    name: String(row?.name ?? "").trim() || `Person ${row?.person_id ?? row?.personId ?? ""}`,
    email: String(row?.email ?? "").trim(),
    nationalRegistration: String(
      row?.national_registration ?? row?.nationalRegistration ?? "",
    ).replace(/\D/g, "") || null,
    instanceId: instanceSelectionId,
    baasInstanceId: Number.isFinite(numericInstanceId) ? numericInstanceId : null,
    groupId,
    groupName: row?.group_name ?? row?.groupName ?? null,
    role,
    permissions,
    status,
    accessStatus,
    lastAccess: row?.last_access ?? row?.lastAccess ?? null,
    hasAccount: row?.has_account === true || row?.hasAccount === true,
    loggedIn: row?.logged_in === true || row?.loggedIn === true,
    canViewProduction: row?.can_view_production !== false && row?.canViewProduction !== false,
    canViewHomolog: row?.can_view_homolog !== false && row?.canViewHomolog !== false,
  };
};

const mapBaasGroup = (row, instanceSelectionId) => {
  const numericInstanceId = Number(row?.instance_id ?? row?.instanceId);
  const roleRaw = String(row?.role ?? row?.roles?.[0] ?? "viewer").trim().toLowerCase();
  const role = normalizeConsoleRole(roleRaw, "viewer");
  const permissions = Array.isArray(row?.permissions)
    ? row.permissions.map(String)
    : BAAS_GROUP_ROLE_PERMISSIONS[role] ?? [];

  return {
    id: String(row?.id ?? row?.baas_api_instance_group_id ?? ""),
    name: String(row?.name ?? "").trim(),
    description: String(row?.description ?? "").trim(),
    instanceId: instanceSelectionId,
    baasInstanceId: Number.isFinite(numericInstanceId) ? numericInstanceId : null,
    role,
    roles: [role],
    permissions,
    memberCount: Number(row?.member_count ?? row?.memberCount) || 0,
    active: row?.active !== false,
  };
};

/**
 * Aggregate membership rows into one person card (org Users page).
 * @param {Array} memberships
 */
export const aggregateBaasUsersByPerson = (memberships = []) => {
  const byKey = new Map();

  for (const membership of memberships) {
    const key =
      membership.personId != null
        ? `p:${membership.personId}`
        : `e:${String(membership.email ?? "").toLowerCase()}`;
    if (!key || key === "p:null" || key === "e:") continue;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        personId: membership.personId,
        entityId: membership.entityId ?? null,
        name: membership.name,
        email: membership.email,
        nationalRegistration: membership.nationalRegistration ?? null,
        lastAccess: membership.lastAccess,
        accessStatus: membership.accessStatus ?? "pending",
        hasAccount: Boolean(membership.hasAccount),
        loggedIn: Boolean(membership.loggedIn),
        memberships: [membership],
      });
      continue;
    }

    existing.memberships.push(membership);
    if (!existing.name && membership.name) existing.name = membership.name;
    if (!existing.email && membership.email) existing.email = membership.email;
    if (!existing.entityId && membership.entityId) existing.entityId = membership.entityId;
    if (!existing.nationalRegistration && membership.nationalRegistration) {
      existing.nationalRegistration = membership.nationalRegistration;
    }
    // Prefer completed when any membership reports it.
    if (
      membership.accessStatus === "completed" ||
      (membership.accessStatus === "pending" && existing.accessStatus === "no_email")
    ) {
      existing.accessStatus = membership.accessStatus;
    }
    const prev = existing.lastAccess ? new Date(existing.lastAccess).getTime() : 0;
    const next = membership.lastAccess ? new Date(membership.lastAccess).getTime() : 0;
    if (next > prev) existing.lastAccess = membership.lastAccess;
    if (membership.hasAccount) existing.hasAccount = true;
    if (membership.loggedIn) existing.loggedIn = true;
  }

  return Array.from(byKey.values()).sort((a, b) =>
    String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, { sensitivity: "base" }),
  );
};

/**
 * Numeric Prod/HML catalog id for MobileService portal routes
 * (`/v1/ib/baas/instances/{id}/users`). Unlike {@link resolveBaasProxyContext},
 * environment is optional — portal memberships are resolved server-side.
 * @param {object | string | number} target
 */
const resolvePortalInstanceId = (target) => {
  if (target != null && typeof target === "object") {
    const numeric = Number(
      target.baasInstanceId ?? target.instanceId ?? target.instance_id,
    );
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    if (target.id != null && target.id !== "") {
      return resolvePortalInstanceId(target.id);
    }
  }

  const raw = String(target ?? "");
  const separator = raw.indexOf(":");
  if (separator > 0) {
    const numeric = Number(raw.slice(0, separator));
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }

  const plain = Number(raw);
  if (Number.isFinite(plain) && plain > 0) return plain;

  const err = new Error("Invalid BaaS instance id for users portal");
  err.name = "BaasConsoleError";
  err.code = "instance_invalid";
  throw err;
};

/**
 * Lists BaasApiInstanceUsers for one instance, or across all given instances (org).
 * Prefers org endpoint when no single instance is selected.
 * Instance page must pass `instance` — never fans out to org `/users`.
 * @param {object | null} instance
 * @param {{ instances?: object[] }} [options]
 */
export const fetchBaasUsers = async (instance, options = {}) => {
  // Instance-scoped Administração: only the selected card's portal id.
  if (instance) {
    const portalInstanceId = resolvePortalInstanceId(instance);
    const data = await mobileConsoleRequest({
      method: "GET",
      path: `v1/ib/baas/instances/${portalInstanceId}/users`,
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    const selectionId = instance.id ?? instance;
    return {
      // Keep the selection card id (HML vs Prod) for instance-scoped UI.
      // API may return the Prod anchor id (Option B) — do not drop those rows.
      items: items.map((row) => mapBaasUser(row, selectionId)),
      manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported ?? false),
    };
  }

  if (Array.isArray(options.instances) && options.instances.length > 0) {
    try {
      const data = await mobileConsoleRequest({
        method: "GET",
        path: "v1/ib/baas/users",
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      return {
        items: items.map((row) => {
          const mapped = mapBaasUser(row, null);
          const match = findInstanceByBaasId(options.instances, mapped.baasInstanceId);
          return { ...mapped, instanceId: match?.id ?? mapped.baasInstanceId };
        }),
        manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported ?? false),
      };
    } catch (orgError) {
      // Fall through to per-instance fan-out; keep error if fan-out also empty.
      options = { ...options, _orgListError: orgError };
    }
  }

  const allTargets = Array.isArray(options.instances) ? options.instances : [];
  // Memberships live on Prod anchors — skip HML twin cards to avoid duplicate rows
  // and wrong "(HML)" locked-link labels in org edit.
  const prodTargets = allTargets.filter((item) => isProductionInstance(item));
  const targets = prodTargets.length > 0 ? prodTargets : allTargets;

  if (targets.length === 0) {
    if (options._orgListError) throw options._orgListError;
    return { items: [], manageSupported: false };
  }

  const results = await Promise.all(
    targets.map(async (target) => {
      try {
        const portalInstanceId = resolvePortalInstanceId(target);
        const data = await mobileConsoleRequest({
          method: "GET",
          path: `v1/ib/baas/instances/${portalInstanceId}/users`,
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        return {
          items: items.map((row) => {
            const mapped = mapBaasUser(row, target.id ?? target);
            const match = findInstanceByBaasId(
              options.instances ?? allTargets,
              mapped.baasInstanceId,
            );
            return match ? { ...mapped, instanceId: match.id } : mapped;
          }),
          manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported ?? false),
          ok: true,
        };
      } catch (error) {
        return { items: [], manageSupported: false, ok: false, error };
      }
    }),
  );

  const items = results.flatMap((result) => result.items);
  if (items.length === 0 && results.every((result) => result.ok === false)) {
    const firstError = results.find((result) => result.error)?.error ?? options._orgListError;
    if (firstError) throw firstError;
  }

  return {
    items,
    manageSupported: results.some((result) => result.manageSupported),
  };
};

/**
 * @param {object} instance
 * @param {{
 *   nationalRegistration?: string,
 *   email?: string,
 *   name?: string,
 *   middleName?: string,
 *   lastName?: string,
 *   phone?: string,
 *   phoneCountryDialCode?: string,
 *   phoneAreaCode?: string,
 *   phoneLocalNumber?: string,
 *   dateOfBirth?: string,
 *   groupId?: string|number|null,
 *   role?: string,
 *   permissions?: string[],
 *   status?: string,
 *   canViewProduction?: boolean,
 *   canViewHomolog?: boolean,
 * }} payload
 */
export const createBaasUser = async (instance, payload) => {
  const ctx = resolveBaasProxyContext(instance);
  const documentDigits = String(payload?.nationalRegistration ?? "").replace(/\D/g, "");
  const phoneCountry = String(payload?.phoneCountryDialCode ?? "").replace(/\D/g, "");
  const phoneArea = String(payload?.phoneAreaCode ?? "").replace(/\D/g, "");
  const phoneLocal = String(payload?.phoneLocalNumber ?? "").replace(/\D/g, "");
  const phoneDigits =
    String(payload?.phone ?? "").replace(/\D/g, "") ||
    (phoneCountry && phoneArea && phoneLocal ? `${phoneCountry}${phoneArea}${phoneLocal}` : "");
  const data = await mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users`,
    body: {
      national_registration: documentDigits || undefined,
      email: payload?.email,
      name: payload?.name,
      middle_name: payload?.middleName,
      last_name: payload?.lastName,
      phone: phoneDigits || undefined,
      phone_country_dial_code: phoneCountry || undefined,
      phone_area_code: phoneArea || undefined,
      phone_local_number: phoneLocal || undefined,
      date_of_birth: payload?.dateOfBirth,
      group_id: payload?.groupId ? Number(payload.groupId) : null,
      role: payload?.role,
      permissions: payload?.permissions,
      status: payload?.status ?? "active",
      can_view_production: payload?.canViewProduction,
      can_view_homolog: payload?.canViewHomolog,
    },
  });
  return mapBaasUser(data, instance.id ?? instance);
};

/**
 * Lookup AccountPerson by CPF/CNPJ before linking as a console user.
 * @param {string} nationalRegistration
 * @param {{ instanceId?: number|string|null }} [options]
 */
export const lookupBaasPersonByDocument = async (nationalRegistration, options = {}) => {
  const digits = String(nationalRegistration ?? "").replace(/\D/g, "");
  const query = { national_registration: digits };
  const instanceId = Number(options.instanceId);
  if (Number.isFinite(instanceId) && instanceId > 0) {
    query.instance_id = instanceId;
  }

  const data = await mobileConsoleRequest({
    method: "GET",
    path: "v1/ib/baas/users/lookup",
    query,
  });

  const person = data?.person ?? null;
  const address = person?.address ?? null;
  const userIdRaw = Number(data?.user_id ?? data?.userId);
  return {
    found: Boolean(data?.found),
    verified: Boolean(data?.verified),
    alreadyMember: Boolean(data?.already_member ?? data?.alreadyMember),
    hasPlatformAccess: Boolean(data?.has_platform_access ?? data?.hasPlatformAccess),
    accessStatus: String(data?.access_status ?? data?.accessStatus ?? "pending"),
    userId: Number.isFinite(userIdRaw) && userIdRaw > 0 ? userIdRaw : null,
    hasPersonEntity: Boolean(data?.has_person_entity ?? data?.hasPersonEntity),
    nationalRegistration: String(data?.national_registration ?? digits),
    person: person
      ? {
          personId: Number(person.person_id ?? person.personId) || null,
          name: String(person.name ?? "").trim(),
          middleName: String(person.middle_name ?? person.middleName ?? "").trim(),
          lastName: String(person.last_name ?? person.lastName ?? "").trim(),
          email: String(person.email ?? "").trim(),
          nationalRegistration: String(
            person.national_registration ?? person.nationalRegistration ?? digits,
          ).replace(/\D/g, ""),
          dateOfBirth: String(person.date_of_birth ?? person.dateOfBirth ?? "").trim(),
          motherName: String(person.mother_name ?? person.motherName ?? "").trim(),
          phoneNumber: String(person.phone_number ?? person.phoneNumber ?? "").trim(),
          phoneCountryDialCode: String(
            person.phone_country_dial_code ?? person.phoneCountryDialCode ?? "",
          ).replace(/\D/g, ""),
          phoneAreaCode: String(person.phone_area_code ?? person.phoneAreaCode ?? "").replace(
            /\D/g,
            "",
          ),
          phoneLocalNumber: String(
            person.phone_local_number ?? person.phoneLocalNumber ?? "",
          ).replace(/\D/g, ""),
          address: address
            ? {
                line1: String(address.line_1 ?? address.line1 ?? "").trim(),
                number: String(address.number ?? "").trim(),
                neighborhood: String(
                  address.neighborhood ?? address.district ?? "",
                ).trim(),
                city: String(address.city ?? "").trim(),
                state: String(address.state ?? "").trim(),
                postalCode: String(address.postal_code ?? address.postalCode ?? "").trim(),
                countryCode: String(address.country_code ?? address.countryCode ?? "").trim(),
              }
            : null,
          entityId:
            person.entity_id != null && String(person.entity_id).trim() !== ""
              ? String(person.entity_id).trim()
              : person.entityId != null && String(person.entityId).trim() !== ""
                ? String(person.entityId).trim()
                : null,
        }
      : null,
  };
};

/**
 * Create/update memberships for `selectedInstances`.
 * Unlinking is explicit only (`membershipsToRemove` or `removeUnselected=true`) —
 * linked instances stay bound unless deactivated or removed via dedicated actions.
 *
 * @param {{
 *   nationalRegistration?: string,
 *   email?: string,
 *   name?: string,
 *   middleName?: string,
 *   lastName?: string,
 *   phone?: string,
 *   dateOfBirth?: string,
 *   role?: string,
 *   permissions?: string[],
 *   status?: string,
 *   selectedInstances: object[],
 *   allInstances: object[],
 *   existingMemberships?: Array,
 *   membershipsToRemove?: Array,
 *   removeUnselected?: boolean,
 * }} params
 */
export const syncBaasUserInstanceAccess = async ({
  nationalRegistration,
  email,
  name,
  middleName,
  lastName,
  phone,
  phoneCountryDialCode,
  phoneAreaCode,
  phoneLocalNumber,
  dateOfBirth,
  role,
  permissions,
  status = "active",
  canViewProduction,
  canViewHomolog,
  selectedInstances = [],
  allInstances = [],
  existingMemberships = [],
  membershipsToRemove = [],
  removeUnselected = false,
}) => {
  const documentDigits = String(nationalRegistration ?? "").replace(/\D/g, "");
  const emailTrimmed = email?.trim() ?? "";
  if (!documentDigits && !emailTrimmed) {
    throw new Error("document_required");
  }

  const selectedIds = new Set(selectedInstances.map((item) => String(item.id)));
  const removeIds = new Set(
    (membershipsToRemove ?? []).map((m) => String(m.id ?? m)).filter(Boolean),
  );
  const byInstanceId = new Map(
    existingMemberships.map((m) => [String(m.instanceId), m]),
  );

  const phoneCountry = String(phoneCountryDialCode ?? "").replace(/\D/g, "");
  const phoneArea = String(phoneAreaCode ?? "").replace(/\D/g, "");
  const phoneLocal = String(phoneLocalNumber ?? "").replace(/\D/g, "");
  const phoneDigits =
    String(phone ?? "").replace(/\D/g, "") ||
    (phoneCountry && phoneArea && phoneLocal ? `${phoneCountry}${phoneArea}${phoneLocal}` : "");

  const identityBody = {
    national_registration: documentDigits || undefined,
    email: emailTrimmed || undefined,
    name,
    middle_name: middleName,
    last_name: lastName,
    phone: phoneDigits || undefined,
    phone_country_dial_code: phoneCountry || undefined,
    phone_area_code: phoneArea || undefined,
    phone_local_number: phoneLocal || undefined,
    date_of_birth: dateOfBirth,
  };

  if (existingMemberships.length === 0 && (selectedInstances.length > 0 || role === "super_user")) {
    try {
      const data = await mobileConsoleRequest({
        method: "POST",
        path: "v1/ib/baas/users",
        body: {
          ...identityBody,
          instance_ids: selectedInstances
            .map((item) => Number(item.baasInstanceId ?? resolveBaasProxyContext(item).instanceId))
            .filter((id) => Number.isFinite(id) && id > 0),
          role,
          permissions,
          status,
        },
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      if (items.length > 0) {
        return items.map((row) => {
          const mapped = mapBaasUser(row, null);
          const match = findInstanceByBaasId(
            selectedInstances.length ? selectedInstances : allInstances,
            mapped.baasInstanceId,
          );
          return { ...mapped, instanceId: match?.id ?? mapped.baasInstanceId };
        });
      }
    } catch {
      // Fall through to per-instance writes.
    }
  }

  if (selectedInstances.length === 0 && role === "super_user") {
    return [];
  }

  const saved = [];

  for (const instance of selectedInstances) {
    const existing = byInstanceId.get(String(instance.id));
    if (existing?.id && removeIds.has(String(existing.id))) continue;
    const payload = {
      nationalRegistration: documentDigits || undefined,
      email: emailTrimmed || undefined,
      name,
      middleName,
      lastName,
      phone: phoneDigits || undefined,
      phoneCountryDialCode: phoneCountry || undefined,
      phoneAreaCode: phoneArea || undefined,
      phoneLocalNumber: phoneLocal || undefined,
      dateOfBirth,
      role,
      permissions,
      status: existing?.id ? (status ?? existing.status) : status,
      groupId: null,
      canViewProduction,
      canViewHomolog,
    };
    if (existing?.id) {
      saved.push(await updateBaasUser(instance, existing.id, payload));
    } else {
      saved.push(await createBaasUser(instance, payload));
    }
  }

  for (const membership of existingMemberships) {
    const explicitRemove = removeIds.has(String(membership.id));
    const uncheckedRemove =
      removeUnselected && !selectedIds.has(String(membership.instanceId));
    if (!explicitRemove && !uncheckedRemove) continue;
    const instance =
      allInstances.find((item) => String(item.id) === String(membership.instanceId)) ??
      findInstanceByBaasId(allInstances, membership.baasInstanceId);
    if (!instance || !membership.id) continue;
    await removeBaasUser(instance, membership.id);
  }

  return saved;
};

/**
 * @param {object} instance
 * @param {string|number} userId
 * @param {{ groupId?: string|number|null, role?: string, permissions?: string[], status?: string }} payload
 */
export const updateBaasUser = async (instance, userId, payload) => {
  const ctx = resolveBaasProxyContext(instance);
  const data = await mobileConsoleRequest({
    method: "PATCH",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${userId}`,
    body: {
      group_id: payload?.groupId ? Number(payload.groupId) : null,
      role: payload?.role,
      permissions: payload?.permissions,
      status: payload?.status,
      can_view_production: payload?.canViewProduction,
      can_view_homolog: payload?.canViewHomolog,
    },
  });
  return mapBaasUser(data, instance.id ?? instance);
};

/** Hard-unlink membership (`DELETE` removes the BaasApiInstanceUsers row). */
export const removeBaasUser = async (instance, userId) => {
  const ctx = resolveBaasProxyContext(instance);
  await mobileConsoleRequest({
    method: "DELETE",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${userId}`,
  });
};

/** Force-revoke all sessions for a console membership (`PersonSessionRevoker`). */
export const logoutBaasConsoleUser = async (instance, userId) => {
  if (!instance || userId == null || userId === "") {
    const err = new Error("instance and userId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const ctx = resolveBaasProxyContext(instance);
  return mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${encodeURIComponent(userId)}/logout`,
  });
};

/**
 * Org Users: membership may only have Prod `baasInstanceId` (not a selection card).
 */
export const logoutBaasConsoleUserByPortalId = async (portalInstanceId, userId) => {
  const numeric = Number(portalInstanceId);
  if (!Number.isFinite(numeric) || numeric <= 0 || userId == null || userId === "") {
    const err = new Error("instance and userId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${numeric}/users/${encodeURIComponent(userId)}/logout`,
  });
};

/**
 * Re-send console access invite (grant if they already have a Valut login, else primeiro acesso).
 * `POST v1/ib/baas/instances/{id}/users/{userId}/access-email/resend`
 */
export const resendBaasUserAccessEmail = async (instance, userId) => {
  if (!instance || userId == null || userId === "") {
    const err = new Error("instance and userId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const ctx = resolveBaasProxyContext(instance);
  return mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${encodeURIComponent(userId)}/access-email/resend`,
  });
};

/**
 * Admin force-set login password for a console membership's AccountPerson.
 * `POST v1/ib/baas/instances/{id}/users/{userId}/password`
 * @param {object} instance
 * @param {string|number} userId
 * @param {{ password: string, confirmPassword: string }} payload
 */
export const setBaasUserLoginPassword = async (instance, userId, payload) => {
  if (!instance || userId == null || userId === "") {
    const err = new Error("instance and userId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const ctx = resolveBaasProxyContext(instance);
  return mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${encodeURIComponent(userId)}/password`,
    body: {
      password: payload?.password,
      confirm_password: payload?.confirmPassword,
    },
  });
};

/**
 * Admin force-set transactional PIN (4 digits) for a console membership's AccountPerson.
 * `POST v1/ib/baas/instances/{id}/users/{userId}/transactional-password`
 * @param {object} instance
 * @param {string|number} userId
 * @param {{ password: string, confirmPassword: string }} payload
 */
export const setBaasUserTransactionalPassword = async (instance, userId, payload) => {
  if (!instance || userId == null || userId === "") {
    const err = new Error("instance and userId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const ctx = resolveBaasProxyContext(instance);
  return mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${encodeURIComponent(userId)}/transactional-password`,
    body: {
      password: payload?.password,
      confirm_password: payload?.confirmPassword,
    },
  });
};

/**
 * Admin wipe of transactional PIN for a console membership's AccountPerson.
 * `DELETE v1/ib/baas/instances/{id}/users/{userId}/transactional-password`
 * @param {object} instance
 * @param {string|number} userId
 */
export const resetBaasUserTransactionalPassword = async (instance, userId) => {
  if (!instance || userId == null || userId === "") {
    const err = new Error("instance and userId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const ctx = resolveBaasProxyContext(instance);
  return mobileConsoleRequest({
    method: "DELETE",
    path: `v1/ib/baas/instances/${ctx.instanceId}/users/${encodeURIComponent(userId)}/transactional-password`,
  });
};

/**
 * @param {object | null} instance
 * @param {{ instances?: object[] }} [options]
 */
export const fetchBaasGroups = async (instance, options = {}) => {
  if (!instance && Array.isArray(options.instances) && options.instances.length > 0) {
    try {
      const data = await mobileConsoleRequest({
        method: "GET",
        path: "v1/ib/baas/groups",
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      const available = Array.isArray(data?.available_permissions)
        ? data.available_permissions.map(String)
        : Array.isArray(data?.availablePermissions)
          ? data.availablePermissions.map(String)
          : [...BAAS_GROUP_PERMISSIONS];
      return {
        items: items.map((row) => {
          const mapped = mapBaasGroup(row, null);
          const match = findInstanceByBaasId(options.instances, mapped.baasInstanceId);
          return { ...mapped, instanceId: match?.id ?? mapped.baasInstanceId };
        }),
        manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported ?? false),
        availablePermissions: available,
      };
    } catch {
      // Fall through to fan-out.
    }
  }

  const targets = instance
    ? [instance]
    : Array.isArray(options.instances)
      ? options.instances
      : [];

  if (targets.length === 0) {
    return {
      items: [],
      manageSupported: false,
      availablePermissions: [...BAAS_GROUP_PERMISSIONS],
    };
  }

  const results = await Promise.all(
    targets.map(async (target) => {
      try {
        const ctx = resolveBaasProxyContext(target);
        const data = await mobileConsoleRequest({
          method: "GET",
          path: `v1/ib/baas/instances/${ctx.instanceId}/groups`,
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        const available = Array.isArray(data?.available_permissions)
          ? data.available_permissions.map(String)
          : Array.isArray(data?.availablePermissions)
            ? data.availablePermissions.map(String)
            : [...BAAS_GROUP_PERMISSIONS];
        return {
          items: items.map((row) => mapBaasGroup(row, target.id ?? target)),
          manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported ?? false),
          availablePermissions: available,
        };
      } catch {
        return {
          items: [],
          manageSupported: false,
          availablePermissions: [...BAAS_GROUP_PERMISSIONS],
        };
      }
    }),
  );

  return {
    items: results.flatMap((result) => result.items),
    manageSupported: results.some((result) => result.manageSupported),
    availablePermissions: results.find((r) => r.availablePermissions?.length)?.availablePermissions
      ?? [...BAAS_GROUP_PERMISSIONS],
  };
};

/**
 * Create the same group definition on one or more instances.
 */
export const createBaasGroupOnInstances = async (instances, payload) => {
  const created = [];
  for (const instance of instances) {
    created.push(await createBaasGroup(instance, payload));
  }
  return created;
};

/**
 * @param {object} instance
 * @param {{ name: string, description?: string, role?: string, permissions?: string[] }} payload
 */
export const createBaasGroup = async (instance, payload) => {
  const ctx = resolveBaasProxyContext(instance);
  const data = await mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/instances/${ctx.instanceId}/groups`,
    body: {
      name: payload?.name,
      description: payload?.description,
      role: payload?.role,
      permissions: payload?.permissions,
    },
  });
  return mapBaasGroup(data, instance.id ?? instance);
};

/**
 * @param {object} instance
 * @param {string|number} groupId
 * @param {{ name?: string, description?: string, role?: string, permissions?: string[], active?: boolean }} payload
 */
export const updateBaasGroup = async (instance, groupId, payload) => {
  const ctx = resolveBaasProxyContext(instance);
  const data = await mobileConsoleRequest({
    method: "PATCH",
    path: `v1/ib/baas/instances/${ctx.instanceId}/groups/${groupId}`,
    body: {
      name: payload?.name,
      description: payload?.description,
      role: payload?.role,
      permissions: payload?.permissions,
      active: payload?.active,
    },
  });
  return mapBaasGroup(data, instance.id ?? instance);
};

export const deleteBaasGroup = async (instance, groupId) => {
  const ctx = resolveBaasProxyContext(instance);
  await mobileConsoleRequest({
    method: "DELETE",
    path: `v1/ib/baas/instances/${ctx.instanceId}/groups/${groupId}`,
  });
};

/** @deprecated Prefer fetchBaasGroups */
export const getMockBaasGroups = () => [];

/** @deprecated Prefer fetchBaasUsers */
export const getMockBaasUsers = () => [];

export const getMockBaasGroupById = () => null;

/** @deprecated Prefer createBaasUser */
export const createMockBaasUser = () => {
  throw new Error("Mock user creation removed — use createBaasUser.");
};

/** @deprecated Prefer updateBaasUser */
export const updateMockBaasUser = () => {
  throw new Error("Mock user update removed — use updateBaasUser.");
};

/** @deprecated Prefer createBaasGroup */
export const createMockBaasGroup = () => {
  throw new Error("Mock group creation removed — use createBaasGroup.");
};

/** @deprecated Prefer updateBaasGroup */
export const updateMockBaasGroup = () => {
  throw new Error("Mock group update removed — use updateBaasGroup.");
};

export const getMockBaasNotificationPrefs = (instanceId) =>
  instanceId
    ? (MOCK_BAAS_NOTIFICATION_PREFS[instanceId] ?? []).map((pref) => ({ ...pref }))
    : (MOCK_BAAS_NOTIFICATION_PREFS["valut-hml"] ?? []).map((pref) => ({ ...pref }));

export const getMockBaasSettings = (instanceId) => ({
  ...(instanceId
    ? MOCK_BAAS_SETTINGS[instanceId]
    : {
        webhookUrl: "",
        timezone: "America/Sao_Paulo",
      }) ?? {
    webhookUrl: "",
    timezone: "America/Sao_Paulo",
  },
});

export const BAAS_SETTINGS_TIMEZONES = [
  "America/Sao_Paulo",
  "America/New_York",
  "Europe/London",
  "UTC",
];
